/**
 * Guard that checks if the current user has entitlement for a specific feature.
 *
 * This guard checks against the user's subscription entitlements to determine if they
 * have access to a particular feature. It handles async loading of user data automatically.
 *
 * Usage Examples:
 *
 * 1. With redirect path (simplest approach):
 * ```typescript
 * {
 *   path: 'smart-devices',
 *   component: SmartDevicesComponent,
 *   canActivate: [HasEntitlementForFeatureGuard],
 *   data: {
 *     feature: EntitlementEnum.SmartDevices,
 *     redirectPath: '/upgrade' // Redirects here if user doesn't have the feature
 *   }
 * }
 * ```
 *
 * 2. With callback function for custom logic:
 * ```typescript
 * {
 *   path: 'owner-statements',
 *   component: OwnerStatementsComponent,
 *   canActivate: [HasEntitlementForFeatureGuard],
 *   data: {
 *     feature: EntitlementEnum.OwnerStatements,
 *     noAccessCallbackFn: () => {
 *       // Custom logic when access is denied
 *       this.dialog.open(UpgradeDialogComponent, {
 *         data: { feature: 'Owner Statements' }
 *       });
 *       // Return false to block navigation
 *       return false;
 *     },
 *     callbackFn: () => {
 *       // Optional: Custom logic when access is granted
 *       console.log('User has access to Owner Statements');
 *       this.analytics.track('OwnerStatementsAccessed');
 *     }
 *   }
 * }
 * ```
 *
 * 3. With dynamic redirect based on entitlement status:
 * ```typescript
 * {
 *   path: 'metrics',
 *   component: MetricsComponent,
 *   canActivate: [HasEntitlementForFeatureGuard],
 *   data: {
 *     feature: EntitlementEnum.Metrics,
 *     noAccessCallbackFn: () => {
 *       // Check if user is on trial or needs to upgrade
 *       const userPlan = this.userService.getCurrentPlan();
 *       if (userPlan === 'trial') {
 *         return this.router.createUrlTree(['/trial-expired']);
 *       } else {
 *         return this.router.createUrlTree(['/upgrade/metrics']);
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * Required Parameters:
 * - `feature`: The EntitlementEnum value to check (required)
 * - Either `redirectPath` or `noAccessCallbackFn` must be provided
 *
 * Optional Parameters:
 * - `callbackFn`: Function to execute when access is granted (success callback)
 *
 * Note: The guard will log an error if the required parameters are not provided.
 */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { UsersFacade } from '@app/shared/+state/users';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HasEntitlementForFeatureGuard {
  constructor(
    private readonly router: Router,
    private readonly usersFacade: UsersFacade
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const { feature, callbackFn: successCallbackFn, redirectPath, noAccessCallbackFn: callbackFn } = route?.data ?? {};

    if (!feature) {
      console.error('You must specify the feature that you want to check entitlement for');
      return false;
    }

    if (!redirectPath && !callbackFn) {
      console.error('You must specify either a noAccessCallbackFn function or a redirect path');
      return false;
    }

    return this.usersFacade.entitlementForFeature$(feature).pipe(
      take(1),
      map((entitlement) => {
        if (entitlement?.available) {
          successCallbackFn?.();
          return true;
        }

        if (callbackFn) {
          return callbackFn();
        }

        this.router.navigate([redirectPath]);
        return this.router.createUrlTree([redirectPath]);
      })
    );
  }
}
